import {useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
} from '@/hooks/sgsst/evidencias/use-evidence'

export function CertificadoPicker({
  file,
  progress,
  onPick,
  onError,
  testIdPrefix,
  className,
}: {
  file: File | null
  progress: number | null
  onPick: (file: File | null) => void
  onError: (message: string | null) => void
  testIdPrefix: string
  className?: string
}) {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  function pick(files: FileList | null) {
    const next = files?.[0]
    if (!next) return
    onError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(next.type)) return onError(t('errors.evidenceBadFormat'))
    if (next.size > EVIDENCE_MAX_BYTES) return onError(evidenceTooBig(t, next))
    onPick(next)
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={EVIDENCE_ACCEPT}
        hidden
        data-testid={`${testIdPrefix}-input`}
        onChange={(e) => pick(e.target.files)}
      />
      {file ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm"
          data-testid={`${testIdPrefix}-picked`}>
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <button
            type="button"
            className="shrink-0 text-xs font-semibold text-primary hover:underline"
            onClick={() => onPick(null)}>
            {t('jerarquia.clear')}
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={className}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-pick`}>
          {t('incapacidades.reportar.certificadoPick')}
        </Button>
      )}
      {progress !== null && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-track">
          <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
        </div>
      )}
    </>
  )
}
